import { Container, Paragraph, SkeletonLoader } from 'component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPartnerDisplay } from '@sr/utils';
import { type PartnerResItem, type PartnerTreeNodeV2 } from '@sr/types';
import { useLocations } from 'core/lib/Locations';
import { LocationPicker } from 'core/LocationPicker';
import { type PartnerInfo } from 'core/lib/types';
import { isDatalakeRecord } from '../../../../utils/partnerRecordOrigin';
import { SpaceWrapper } from '../../styles';
import { BoxContainer } from '../utils/BoxContainer';

type SelectInvoiceRecipientProps = {
  hierarchiesLoading: boolean;
  selectedHierarchy?: string;
  onRecipientChange: (recipient?: string) => void;
  onAvailableCostCentersChange: (value: boolean) => void;
  getPartnerInfo: (partnerIds: string[]) => Promise<PartnerInfo[]>;
};

const SelectInvoiceRecipient = ({
  hierarchiesLoading,
  selectedHierarchy,
  onRecipientChange,
  onAvailableCostCentersChange,
  getPartnerInfo,
}: SelectInvoiceRecipientProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPartnerDetails, setCurrentPartnerDetails] = useState<PartnerInfo | null>(null);
  const { t } = useTranslation('', { keyPrefix: 'co2_impact.checkout' });
  const locations = useLocations({
    filterServiceName: 'services.follow.up.name',
  });

  const filterCostCenters = useCallback((nodes: PartnerTreeNodeV2[]): PartnerTreeNodeV2[] => {
    const isFno = (partner: PartnerResItem) => isDatalakeRecord(partner.source);
    const isBusinessPartner = (partner: PartnerResItem) => partner.type === 'BUSINESSPARTNER';

    return nodes
      .filter((tree) => (isFno(tree.item) ? isBusinessPartner(tree.item) : true))
      .map((node) => ({
        ...node,
        children: filterCostCenters(node.children),
      }));
  }, []);

  const availableCostCenters = useMemo(
    () =>
      filterCostCenters(
        locations.partnerTrees.filter((tree) => tree.item.dwKey === selectedHierarchy),
      ),
    [locations, filterCostCenters, selectedHierarchy],
  );

  useEffect(() => {
    onAvailableCostCentersChange(availableCostCenters.length > 0);
  }, [availableCostCenters, onAvailableCostCentersChange]);

  const handleChange = (dwKeys: string[]) => {
    onRecipientChange(dwKeys[0] || undefined);
    getPartnerInfo(dwKeys)
      .then((res) => {
        setCurrentPartnerDetails(res[0] || null);
      })
      .catch((e: unknown) => {
        console.error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPartnerDetails(null);
  }, [selectedHierarchy]);

  const { nameAndText, addressAndId } = useMemo(() => {
    if (currentPartnerDetails) {
      const p = locations.locationsById[currentPartnerDetails.dwKey];

      return p ? getPartnerDisplay(p) : { nameAndText: '', addressAndId: '' };
    }

    return { nameAndText: '', addressAndId: '' };
  }, [currentPartnerDetails, locations.locationsById]);

  if (loading || hierarchiesLoading) return <LoadingSelectInvoiceRecipient />;

  return (
    <BoxContainer>
      <SpaceWrapper>
        <LocationPicker
          label={t('step2.label')}
          multiSelect={false}
          loading={locations.loading}
          required
          selectedValues={currentPartnerDetails?.dwKey ? [currentPartnerDetails.dwKey] : []}
          onChange={handleChange}
          locations={availableCostCenters}
          showInactiveToggle={false}
          hideInactiveLocations={false}
          allowSelectingBusinessPartners
        />
        {currentPartnerDetails ? (
          <Container outline={true}>
            <Paragraph variant="large">{nameAndText}</Paragraph>
            <Paragraph variant="large">{addressAndId}</Paragraph>
          </Container>
        ) : null}
      </SpaceWrapper>
    </BoxContainer>
  );
};

const LoadingSelectInvoiceRecipient = () => (
  <Container>
    <SkeletonLoader height={25} width={250} />
    <SkeletonLoader height={35} width={300} />
  </Container>
);

export default SelectInvoiceRecipient;
